import { fmt, fmtCount } from '../utils/formatters';

/**
 * The sample Daily Brief in the home page hero. Hand-written — the live demo
 * brief is on /demo — but shaped like the real thing: each move carries the
 * detector that would have found it (so it wears BriefCard's label), and the
 * subject line is summed from the moves so it can never disagree with them.
 * The page says it is a sample; nothing here is a claim about a customer.
 */
export interface SampleMove {
  detector: string;
  title: string;
  why: string;
  low: number;
  high: number;
}

export const SAMPLE_MOVES: SampleMove[] = [
  { detector: 'reprice', title: 'Raise Oat Milk Latte to $5.75', why: 'Sells hardest, priced softest, demand won’t blink.', low: 180, high: 240 },
  { detector: 'bundle', title: 'Bundle Matcha + Banana Bread', why: 'Bought together 38% of the time last month.', low: 210, high: 300 },
  { detector: 'daypart', title: 'Push Cold Brew in the 2–4pm lull', why: 'Your slowest window, your highest-margin drink.', low: 90, high: 140 },
];

/**
 * One move from the demo café, measured after the owner took it: the proof the
 * brief's footer promises ("measured against reality"). Modeled demo figures
 * (output/2026-10-27-brag-video-v3/brag-plan.md), labeled as the demo's record
 * on the page. Not a customer, and not a controlled test.
 */
export const SAMPLE_OUTCOME = {
  title: SAMPLE_MOVES[0].title,
  taken: 'Sep 8',
  weeksLater: 3,
  cupsBefore: 1790,
  cupsAfter: 1760,
  monthlySoFar: 212,
};

/** "3 moves for today, worth $480–$680/mo" — the sums of the moves, nothing typed by hand. */
export function sampleSubject(moves: SampleMove[]): string {
  const low = moves.reduce((sum, m) => sum + m.low, 0);
  const high = moves.reduce((sum, m) => sum + m.high, 0);
  return `${fmtCount(moves.length)} moves for today, worth ${fmt(low)}–${fmt(high)}/mo`;
}

/**
 * Yesterday against the shop's own Tuesdays: the daily score and the usual
 * range the email draws (engine/score.py). `baseline` is the shop's 90-day
 * Tuesday average (snap.dow_avg); `usual` is the last eight Tuesdays.
 */
export const SAMPLE_DAY = {
  weekday: 'Tuesday',
  yesterday: 1342,
  baseline: 1210,
  usual: { min: 960, low: 1090, high: 1310, max: 1420, days: 8 },
};

/** engine/score.py: 50 at exactly typical, one point per 1% above or below, clamped to 0–100. */
export function dailyScore(actual: number, baseline: number): number {
  return Math.max(0, Math.min(100, Math.round(50 + ((actual - baseline) / baseline) * 100)));
}

/** The score bands' headlines, copied from engine/score.py _BANDS. Templates, never a model. */
export function scoreHeadline(score: number, weekday: string): string {
  if (score >= 80) return `A banner day — well above your typical ${weekday}.`;
  if (score >= 60) return `A strong day, ahead of your typical ${weekday}.`;
  if (score >= 40) return `A steady day, right around normal for a ${weekday}.`;
  if (score >= 20) return `A soft day, below your typical ${weekday}.`;
  return `A rough day, well under your typical ${weekday} — worth a look.`;
}
